import { DiscountProvider } from "@redotech/cockroachdb-codegen/congodb/discount-provider";
import {
  ButtonStyleDefinition,
  Enableable,
  InputStyleDefinition,
  Override,
  SocialPlatform,
} from "@redotech/redo-content-builder-common/brand-kit";
import {
  Alignment,
  ButtonHierarchy,
  FontFamily,
} from "@redotech/redo-content-builder-common/element-schema";
import { DiscountGroupType } from "@redotech/redo-discounts-common/discount";
import { UtmParameters } from "@redotech/redo-marketing-core-common/utm";
import { SchemaType } from "./advanced-flow/schemas/schemas";
import { EmailBlock, RecursiveEmailBlock } from "./email-template";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { widthPixelsToPercentage } from "@redotech/redo-marketing-core-common/email-builder/email-sizing";

// alias to avoid having to change the imports in a ton of files
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export type { EmailBlock as Section } from "./email-template";

// Josh - I have no idea what any of this is doing.
// I extracted this from the customer return generation process
// and inferred the types from the context.
export interface CustomerReturnCreatedAttachmentGenerationParameters {
  shipments: {
    postage_label?: string;
    form_label?: string;
    _shipment: { forms?: { form_type?: string; form_url?: string }[] };
    customs_forms?: { commercial_invoices?: { url: string }[] | null } | null;
  }[];
  variables: {
    postage_label?: string;
    form_label?: string;
    commercialInvoice?: string;
  };
  pickup_details: boolean; // all we care about is truthiness, apparently
  manualLabelData?: {
    shipments?: { packingSlipUrl?: string; returnItemIds?: string[] }[];
  };
  additionalShippingProviderDetails?: { labelUrl?: string };
  dropoffQrCodeUrls?: string[];
  qrCodeOnlyShipping?: boolean;
}

/**
 * Emails can contain multiple bodies of content, of the following types
 */
export enum EmailFormat {
  /**
   * Standard HTML email
   */
  STATIC = "static",
  /**
   * AMP email
   */
  AMP = "amp",
  // Apple Mail should NOT be added here.
  // It should instead be rendered inside of static emails, conditionally rendered via @supports queries
}

export enum EmailRenderEnvironment {
  /** Used when rendering an email to send to a customer */
  REAL = "real",
  /** Used when rendering an email in the builder */
  BUILDER = "builder",
  /** Used when previewing an email template or sending a test email */
  PREVIEW = "preview",
  /** Used when sending emails to the seed lists */
  SEED_LIST = "seed-list",
  /** Used when the user clicks "view in browser" */
  VIEW_IN_BROWSER = "view-in-browser",
}

/**
 * Different email clients have differing levels of interactivity.
 * This is different from EmailFormat because both FALLBACK and APPLE_MAIL
 * interactivity take place in a "static" email, but rendered in different clients.
 */
export enum InteractivityContext {
  FALLBACK = "fallback",
  AMP = "amp",
  APPLE_MAIL = "apple-mail",
}

export enum ProductSelectionType {
  DYNAMIC = "dynamic",
  MANUAL = "static",
}

export enum CartLayoutType {
  ROWS = "rows",
  COLUMNS = "columns",
}

export enum ShoppableProductsSize {
  SMALL = "sm",
  MEDIUM = "md",
  LARGE = "lg",
  EXTRA_LARGE = "xl",
}

export namespace ButtonSize {
  export const SMALL = 0.8;
  export const MEDIUM = 1;
  export const LARGE = 1.2;
  export const DEFAULT_HEIGHT = 40;
  export const DEFAULT_WIDTH = 200;
}

export namespace PaddingSize {
  export const SMALL = 8;
  export const MEDIUM = 16;
  export const LARGE = 32;
}

export enum EmailBuilderFontWeight {
  NORMAL = "normal",
  BOLD = "bold",
}

export interface Padding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export enum TemplateType {
  TRANSACTIONAL = "transactional",
  MARKETING = "marketing",
  DEFAULT = "default",
  RECOVER = "recover",
}

export interface Discount {
  _id: string;
  title: string;
  provider: DiscountProvider;
  groupType: DiscountGroupType;
  discountId: string;
}

/**
 * Templates are nested inside of various other objects
 */
export enum TemplateSource {
  TEMPLATE_COLLECTION = "template_collection",
  CAMPAIGN = "campaign",
  // AUTOMATION might go here in the future
}

export function templateTypeLabel(templateType: TemplateType): string {
  switch (templateType) {
    case TemplateType.TRANSACTIONAL:
      return "Transactional";
    case TemplateType.MARKETING:
      return "Marketing";
    case TemplateType.RECOVER:
      return "Recover";
    case TemplateType.DEFAULT:
      return "Default";
  }
}

export enum VerticalAlignment {
  TOP = "top",
  CENTER = "center",
  BOTTOM = "bottom",
}

// {static,dynamic} coincidentally matches unrelated DB enums
// (discount_group_type, product_selection_type) — not a hand-mirror.
//redontIgnore
export enum TableMode {
  STATIC = "static",
  DYNAMIC = "dynamic",
}

// {auto,manual} coincidentally matches unrelated DB enums
// (billing_automation, cycle_count_source) — not a hand-mirror.
//redontIgnore
export enum TableColumnWidthMode {
  AUTO = "auto",
  MANUAL = "manual",
}

// Distinct from VerticalAlignment ("center"): the table value maps straight to
// MJML's vertical-align, which expects "middle".
export enum TableVerticalAlignment {
  TOP = "top",
  MIDDLE = "middle",
  BOTTOM = "bottom",
}

// {solid,dashed,dotted} coincidentally matches the unrelated
// slip_line_style DB enum — not a hand-mirror.
//redontIgnore
export enum TableBorderStyle {
  SOLID = "solid",
  DASHED = "dashed",
  DOTTED = "dotted",
}

export enum TableMobileStacking {
  LEFT_TO_RIGHT = "left-to-right",
  RIGHT_TO_LEFT = "right-to-left",
  NONE = "none",
}

export enum EmailHeaderType {
  IMAGE = "image",
  LOGO = "logo",
  TEXT = "text",
}

export enum Size {
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large",
  CUSTOM = "custom",
}

export enum ButtonLinkType {
  WEB_PAGE = "web-page",
  DYNAMIC_VARIABLE = "dynamic-variable",
}

export enum QrCodeLinkType {
  WEB_PAGE = "web-page",
  DYNAMIC_VARIABLE = "dynamic-variable",
}

export interface Address {
  businessAddress: string;
  legalAddress: string;
  cityStateZip: string;
  country: string;
}

export const FONT_FAMILIES = [
  FontFamily.ARIAL,
  FontFamily.COURIER_NEW,
  FontFamily.GEORGIA,
  FontFamily.LUCIDA_SANS_UNICODE,
  FontFamily.TAHOMA,
  FontFamily.TIMES_NEW_ROMAN,
  FontFamily.TREBUCHET_MS,
  FontFamily.VERDANA,
] as const;

export const alignmentToFlexAlign: Record<Alignment, string> = {
  [Alignment.LEFT]: "flex-start",
  [Alignment.CENTER]: "center",
  [Alignment.RIGHT]: "flex-end",
};

export enum EmailBlockType {
  TRACKABLE_SUMMARY = "order-summary",
  TRACKING_INFO = "tracking-info",
  BUTTON = "button",
  PRODUCTS = "interactive-cart", // value is for backwards compatibility
  HEADER = "header",
  FOOTER = "footer",
  IMAGE = "image",
  TEXT = "text",
  SPACER = "spacer",
  LINE = "line",
  REVIEW_REQUEST = "review-request",
  COLUMN = "column",
  MENU = "menu",
  SOCIALS = "socials",
  DISCOUNT = "discount",
  /**
   * V2 of the interactive cart. Intended to look way flashier and work in both AMP and Apple Mail.
   * Intentionally fully decoupled from V1.
   */
  SHOPPABLE_PRODUCTS = "shoppable-products",
  /**
   * Interactive scratch-to-reveal element in email. Recipients can scratch to reveal hidden content.
   */
  SCRATCH_TO_REVEAL = "scratch-to-reveal",
  /**
   * Interactive review request element in email. Recipients can leave reviews directly from email.
   */
  INTERACTIVE_REVIEW_REQUEST = "interactive-review-request",
  QR_CODE = "qr-code-email",
  SUBSCRIPTION_SKIP_OR_DELAY = "subscription-skip-or-delay",
  SUBSCRIPTION_ADD_TO_ORDER = "subscription-add-to-order",
  SUBSCRIPTION_SWAP_PRODUCT = "subscription-swap-product",
  CHROME_DINO = "chrome-dino",
  SECTION_REFERENCE = "section-reference",
  /**
   * One-click subscription block. Similar to shoppable products, but instead of checkout
   * it creates a subscription for the selected product with one click.
   */
  ONE_CLICK_SUBSCRIPTION = "one-click-subscription",
  /**
   * Klaviyo-parity table block. Renders a list of items — a fixed static grid
   * or (dynamic mode) one row per item in a collection resolved from the event
   * payload via a path. Cells are image or text only.
   */
  TABLE = "table",
}

export enum SocialIconColor {
  BLACK = "black",
  WHITE = "white",
  GRAY = "gray",
}

export enum SocialItemSource {
  CUSTOM = "custom",
  BRANDKIT = "brandkit",
}

export interface MenuItem {
  id: string;
  label: string;
}

export interface SocialItem {
  id: string;
  platform: SocialPlatform;
  url: string;
  hidden?: boolean;
  source?: SocialItemSource;
}

export function isRecursiveEmailBlock(
  section: EmailBlock,
): section is RecursiveEmailBlock {
  return (
    section.type === EmailBlockType.COLUMN ||
    section.type === EmailBlockType.INTERACTIVE_REVIEW_REQUEST
  );
}

// temporary to reduce amount of reimports needed
export { isRecursiveEmailBlock as isRecursiveSection };

export const getButtonSize = (
  size: Size,
  customWidth: number,
  customHeight: number,
) => {
  switch (size) {
    case Size.SMALL:
      return {
        width: ButtonSize.DEFAULT_WIDTH,
        height: ButtonSize.DEFAULT_HEIGHT * ButtonSize.SMALL,
      };
    case Size.MEDIUM:
      return {
        width: ButtonSize.DEFAULT_WIDTH,
        height: ButtonSize.DEFAULT_HEIGHT * ButtonSize.MEDIUM,
      };
    case Size.LARGE:
      return {
        width: ButtonSize.DEFAULT_WIDTH,
        height: ButtonSize.DEFAULT_HEIGHT * ButtonSize.LARGE,
      };
    case Size.CUSTOM:
      return { width: customWidth, height: customHeight };
  }
};

export const getPadding = (
  horizontalPadding: Size,
  verticalPadding: Size,
  customPadding: Padding,
) => {
  let top = customPadding.top,
    right = customPadding.right,
    bottom = customPadding.bottom,
    left = customPadding.left;
  switch (horizontalPadding) {
    case Size.SMALL:
      left = right = PaddingSize.SMALL;
      break;
    case Size.MEDIUM:
      left = right = PaddingSize.MEDIUM;
      break;
    case Size.LARGE:
      left = right = PaddingSize.LARGE;
      break;
  }
  switch (verticalPadding) {
    case Size.SMALL:
      top = bottom = PaddingSize.SMALL;
      break;
    case Size.MEDIUM:
      top = bottom = PaddingSize.MEDIUM;
      break;
    case Size.LARGE:
      top = bottom = PaddingSize.LARGE;
      break;
  }
  return { top, right, bottom, left };
};

export class EmailUtmParameters extends UtmParameters {
  constructor(campaign?: string, contact?: string) {
    super({ source: "redo", medium: "email", campaign, contact });
  }

  override getSource(): string {
    return super.getSource() ?? "";
  }

  override getMedium(): string {
    return super.getMedium() ?? "";
  }
}

export enum ScreenSize {
  DESKTOP = "desktop",
  MOBILE = "mobile",
  /**
   * Custom viewport width for testing responsive behavior at arbitrary widths.
   * When using CUSTOM, you must provide an explicit viewportWidth value.
   */
  CUSTOM = "custom",
}

/**
 * How big the gap is between columns in a column section.
 * TODO: make this a setting in the column section itself.
 */
export const COLUMN_GAP = 24;

/** Canonical default link color for email and tracking-page rich text. */
export const DEFAULT_EMAIL_LINK_COLOR = "#2563eb";

export interface BrandKitEmailButton {
  hierarchy: ButtonHierarchy;
  override: Enableable<Override<ButtonStyleDefinition>>;
  fullWidth: boolean;
  text: string;
}

export interface BrandKitEmailInput {
  override: Enableable<Override<InputStyleDefinition>>;
  fullWidth: boolean;
}

/**
 * Whether an email block is "interactive" in the sense that it has advanced functionality in AMP and Apple Mail.
 *
 * Used to determine whether to render an AMP version of an email, for example.
 */
export const emailBlockIsInteractive: Record<EmailBlockType, boolean> = {
  [EmailBlockType.TABLE]: false,
  [EmailBlockType.SHOPPABLE_PRODUCTS]: true,
  [EmailBlockType.PRODUCTS]: false,
  [EmailBlockType.BUTTON]: false,
  [EmailBlockType.REVIEW_REQUEST]: false,
  [EmailBlockType.TRACKABLE_SUMMARY]: false,
  [EmailBlockType.TRACKING_INFO]: false,
  [EmailBlockType.IMAGE]: false,
  [EmailBlockType.TEXT]: false,
  [EmailBlockType.SPACER]: false,
  [EmailBlockType.LINE]: false,
  [EmailBlockType.HEADER]: false,
  [EmailBlockType.FOOTER]: false,
  [EmailBlockType.MENU]: false,
  [EmailBlockType.SOCIALS]: false,
  [EmailBlockType.DISCOUNT]: false,
  [EmailBlockType.COLUMN]: false,
  [EmailBlockType.SCRATCH_TO_REVEAL]: true,
  [EmailBlockType.INTERACTIVE_REVIEW_REQUEST]: true,
  [EmailBlockType.SUBSCRIPTION_SKIP_OR_DELAY]: true,
  [EmailBlockType.SUBSCRIPTION_ADD_TO_ORDER]: true,
  [EmailBlockType.SUBSCRIPTION_SWAP_PRODUCT]: true,
  [EmailBlockType.CHROME_DINO]: true,
  [EmailBlockType.SECTION_REFERENCE]: false,
  [EmailBlockType.QR_CODE]: false,
  [EmailBlockType.ONE_CLICK_SUBSCRIPTION]: true,
};

/**
 * Blocks that need to be rendered with a different environment when viewed in browser.
 * This helps optimize performance by only rendering with the special environment when needed.
 */
export const blocksThatRenderDifferentlyWhenViewedInBrowser = [
  EmailBlockType.INTERACTIVE_REVIEW_REQUEST,
];

export namespace AppleMailConstants {
  export const RECOMMENDATIONS_CACHE_DURATION_HOURS = 1;
  export const RECOMMENDATIONS_CACHE_KEY_PREFIX = "email-recommendations-";
}

/**
 * https://github.com/ampproject/amphtml/issues/40251
 *
 * `true` means the bug exists
 */
export const AMP_IGNORES_HEIGHTS = true;

export interface ManuallySelectedProduct {
  productId: string;
  variantId: string;
}

export const triggerProvidesProductData = [
  SchemaType.MARKETING_CHECKOUT_ABANDONMENT,
  // SchemaType.MARKETING_BROWSE_ABANDONMENT, // shopify does not provide cart data for browse abandonment
  SchemaType.MARKETING_CART_ABANDONMENT,
  SchemaType.MARKETING_BACK_IN_STOCK,
];

export enum ScratcherSize {
  EXTRA_SMALL = "xs",
  SMALL = "sm",
  MEDIUM = "md",
  LARGE = "lg",
  EXTRA_LARGE = "xl",
}

/**
 * Adds a doctype to the HTML string if it doesn't already have one.
 * The backend renderer seems to do this automatically, but the frontend doesn't.
 */
export function ensureDoctype(html: string): string {
  return html.toLowerCase().startsWith("<!doctype html>")
    ? html
    : `<!DOCTYPE html>${html}`;
}

export enum Orientation {
  HORIZONTAL = "horizontal",
  VERTICAL = "vertical",
}

export enum ObjectFit {
  COVER = "cover",
  CONTAIN = "contain",
}

export const SUBSCRIPTION_SKIP_OR_DELAY_ENABLED = true;
export const SUBSCRIPTION_ADD_TO_ORDER_ENABLED = false;
export const SUBSCRIPTION_SWAP_PRODUCT_ENABLED = false;
export const CHROME_DINO_ENABLED = false;

/**
 * Putting this string into an email's HTML will cause SES to replace it with a tracking pixel, instead of appending it to the end of the email.
 * https://docs.aws.amazon.com/ses/latest/dg/faqs-metrics.html#:~:text=by%20inserting%20the-,%7B%7Bses%3AopenTracker%7D%7D,-placeholder%20into%20the
 *
 * TODO: consider wrapping in a <div style="color:transparent;height:1px;width:1px;"> or something, as a last line of defense
 * against accidentally showing the literal "openTracker" at the top of emails if something goes wrong.
 * Needs more research to see what the ideal style attributes would be for that div, though. E.g., would 0px height affect image loading?
 */
export const SES_TRACKING_PIXEL_PLACEHOLDER = "{{ses:openTracker}}";
export function removeSesTrackingPixelPlaceholder(html: string): string {
  // hide the ses tracking pixel placeholder, but don't delete it, for better file size estimation
  if (html.includes(SES_TRACKING_PIXEL_PLACEHOLDER)) {
    return html.replace(
      SES_TRACKING_PIXEL_PLACEHOLDER,
      "<!-- SES Tracking Pixel Placeholder -->",
    );
  }
  return html;
}

/** Used as a workaround when we can't add data attributes to elements directly (cough MJML cough) */
export const TRACKING_ATTRIBUTES_CLASS_PREFIX = "redo-track-";
export const TRACKING_ATTRIBUTES_DELIMITER = "|||";
